import { useI18n } from 'vue-i18n'

interface ExcludedPageSeo {
  title: string
  description: string
}

interface ExcludedPagesConfig {
  [path: string]: ExcludedPageSeo
}

export const useSeoExcludedPages = () => {
  const { t } = useI18n()

  const excludedPagesConfig = computed<ExcludedPagesConfig>(() => ({
    '/login': {
      title: t('seo.login.title'),
      description: t('seo.login.description')
    },
    '/password/reset': {
      title: t('seo.password_reset.title'),
      description: t('seo.password_reset.description')
    },
    '/register': {
      title: t('seo.register.title'),
      description: t('seo.register.description')
    },
    '/register/success': {
      title: t('seo.register_success.title'),
      description: t('seo.register_success.description')
    },
    '/panel': {
      title: t('seo.panel.title'),
      description: t('seo.panel.description')
    },
    '/panel/profile': {
      title: t('seo.panel_profile.title'),
      description: t('seo.panel_profile.description')
    },
    '/panel/profile/edit': {
      title: t('seo.panel_profile_edit.title'),
      description: t('seo.panel_profile_edit.description')
    },
    '/panel/favorites': {
      title: t('seo.panel_favorites.title'),
      description: t('seo.panel_favorites.description')
    },
    '/panel/credit-lock': {
      title: t('seo.panel_credit_lock.title'),
      description: t('seo.panel_credit_lock.description')
    }
  }))

  // Helper function to normalize path by removing language prefix and trailing slash
  const normalizePath = (path: string): string => {
    // Remove language prefix pattern: /ky/ or /en/ or any other 2-letter language code
    let normalized = path.replace(/^\/([a-z]{2})\//, '/').replace(/^\/([a-z]{2})$/, '/')

    // Remove trailing slash if not root
    if (normalized !== '/' && normalized.endsWith('/')) {
      normalized = normalized.slice(0, -1)
    }

    return normalized
  }

  const isExcludedPage = (path: string): boolean => {
    // Check direct match first (normalized just in case input has issues)
    const normalizedInput = normalizePath(path)

    return Object.keys(excludedPagesConfig.value).includes(normalizedInput)
  }

  const getExcludedPageSeo = (path: string): ExcludedPageSeo | null => {
    const normalizedPath = normalizePath(path)
    return excludedPagesConfig.value[normalizedPath] || null
  }

  const getExcludedPages = (): string[] => {
    return Object.keys(excludedPagesConfig.value)
  }

  return {
    isExcludedPage,
    getExcludedPageSeo,
    getExcludedPages
  }
}
